import { useEffect, useState } from 'react'
import { Smartphone, Loader2, Copy, RefreshCw, ShieldOff, ShieldCheck } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { api, getApiErrorMessage } from '@/lib/api'
import { toast } from 'sonner'
import { QRCodeSVG } from 'qrcode.react'

interface SetupData {
  secret: string
  qr_code_uri: string
  backup_codes: string[]
  message: string
}

// CHECKPOINT: PRD-09 FR-9.10 — Superadmin TOTP 2FA setup and backup codes
export function TwoFactorTab() {
  const [loading, setLoading] = useState(true)
  const [enabled, setEnabled] = useState(false)
  const [setup, setSetup] = useState<SetupData | null>(null)
  const [code, setCode] = useState('')
  const [verifying, setVerifying] = useState(false)
  const [codes, setCodes] = useState<string[] | null>(null)
  const [regenerating, setRegenerating] = useState(false)
  const [disabling, setDisabling] = useState(false)

  useEffect(() => {
    fetchStatus()
  }, [])

  const fetchStatus = async () => {
    setLoading(true)
    try {
      const res = await api.get('/admin/me')
      setEnabled(res.data.totp_enabled)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to load 2FA status'))
    } finally {
      setLoading(false)
    }
  }

  const startSetup = async () => {
    try {
      const res = await api.post('/admin/security/2fa/setup')
      setSetup(res.data)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to start 2FA setup'))
    }
  }

  const verifyAndEnable = async () => {
    if (code.length !== 6) {
      toast.error('Enter the 6-digit code')
      return
    }
    setVerifying(true)
    try {
      const res = await api.post('/admin/security/2fa/verify', { code })
      if (res.data.verified) {
        toast.success('2FA enabled')
        setEnabled(true)
        setSetup(null)
        setCode('')
      } else {
        toast.error('Invalid code')
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to verify code'))
    } finally {
      setVerifying(false)
    }
  }

  const regenerateCodes = async () => {
    setRegenerating(true)
    try {
      const res = await api.post('/admin/security/2fa/recovery-codes')
      setCodes(res.data)
      toast.success('Backup codes regenerated')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to regenerate codes'))
    } finally {
      setRegenerating(false)
    }
  }

  const disable2FA = async () => {
    setDisabling(true)
    try {
      await api.post('/admin/security/2fa/disable')
      toast.success('2FA disabled')
      setEnabled(false)
      setSetup(null)
      setCodes(null)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to disable 2FA'))
    } finally {
      setDisabling(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Smartphone className="h-4 w-4" />
            Two-Factor Authentication
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {enabled ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-emerald-500">
                <ShieldCheck className="h-5 w-5" />
                <p className="text-sm font-medium">2FA is enabled</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={regenerateCodes} disabled={regenerating} size="sm" variant="outline">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Regenerate Backup Codes
                </Button>
                <Button onClick={disable2FA} disabled={disabling} size="sm" variant="destructive">
                  <ShieldOff className="h-4 w-4 mr-2" />
                  Disable 2FA
                </Button>
              </div>
              {codes && (
                <div className="rounded-md border border-border p-3 space-y-2">
                  <p className="text-xs text-muted-foreground">Save these backup codes somewhere safe. They will only be shown once.</p>
                  <div className="grid grid-cols-2 gap-2 font-mono text-xs">
                    {codes.map((c, i) => (
                      <div key={i} className="bg-muted px-2 py-1 rounded">{c}</div>
                    ))}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(codes.join('\n'))
                      toast.success('Backup codes copied')
                    }}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy All
                  </Button>
                </div>
              )}
            </div>
          ) : setup ? (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Scan the QR code with your authenticator app, then enter the 6-digit code to enable 2FA.
              </p>
              <div className="inline-block rounded-md bg-white p-2">
                <QRCodeSVG value={setup.qr_code_uri} size={180} />
              </div>
              <div className="flex items-center gap-2">
                <Input readOnly value={setup.secret} className="font-mono text-xs max-w-sm" />
                <Button
                  size="icon"
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(setup.secret)
                    toast.success('Secret copied')
                  }}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex gap-2">
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="6-digit code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="max-w-xs"
                />
                <Button onClick={verifyAndEnable} disabled={verifying}>
                  {verifying && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Enable 2FA
                </Button>
                <Button variant="ghost" onClick={() => setSetup(null)}>Cancel</Button>
              </div>
              {setup.backup_codes && setup.backup_codes.length > 0 && (
                <div className="rounded-md border border-border p-3">
                  <p className="text-xs text-muted-foreground mb-2">Backup codes (shown once):</p>
                  <div className="grid grid-cols-2 gap-2 font-mono text-xs">
                    {setup.backup_codes.map((c, i) => (
                      <div key={i} className="bg-muted px-2 py-1 rounded">{c}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Two-factor authentication is required for all superadmin accounts. Set it up now.
              </p>
              <Button onClick={startSetup} size="sm">
                Set Up 2FA
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
